import { Controller } from "@hotwired/stimulus";

type MobilePane = "list" | "detail" | "chat";

type HtmxHistoryEvent = Event & {
    detail?: {
        path?: string;
    };
};

const MOBILE_QUERY = "(max-width: 1023.98px)";
const EXPLICIT_PANE_INTENT_TTL_MS = 3000;

type ExplicitPaneIntent = {
    pane: MobilePane;
    ticketId: string;
    expiresAt: number;
};

export default class extends Controller<HTMLElement> {
    static targets = ["list", "workspace", "detail", "chat"];
    static values = {
        initialPane: String,
    };

    declare readonly listTarget: HTMLElement;
    declare readonly workspaceTarget: HTMLElement;
    declare readonly detailTarget: HTMLElement;
    declare readonly chatTarget: HTMLElement;
    declare readonly hasListTarget: boolean;
    declare readonly hasWorkspaceTarget: boolean;
    declare readonly hasDetailTarget: boolean;
    declare readonly hasChatTarget: boolean;
    declare readonly initialPaneValue: string;

    private mediaQuery: MediaQueryList | null = null;
    private explicitPaneIntent: ExplicitPaneIntent | null = null;

    private readonly showReplyChat = (event: Event): void => {
        const ticketId = (event as CustomEvent<{ ticketId: string }>).detail.ticketId;
        if (this.mediaQuery?.matches && ticketId === this.currentTicketId(new URL(window.location.href))) {
            this.showCurrentChat();
        }
    };

    connect(): void {
        this.syncFromLocation = this.syncFromLocation.bind(this);
        this.syncFromHistoryEvent = this.syncFromHistoryEvent.bind(this);
        this.syncAria = this.syncAria.bind(this);

        this.mediaQuery = window.matchMedia(MOBILE_QUERY);
        this.mediaQuery.addEventListener("change", this.syncAria);

        document.body.addEventListener("htmx:pushedIntoHistory", this.syncFromHistoryEvent);
        document.body.addEventListener("htmx:replacedInHistory", this.syncFromHistoryEvent);
        window.addEventListener("popstate", this.syncFromLocation);
        document.body.addEventListener("ticket-reply-sent", this.showReplyChat);

        this.applyPane(this.initialPane(), { replaceHistory: true });
    }

    disconnect(): void {
        this.mediaQuery?.removeEventListener("change", this.syncAria);
        document.body.removeEventListener("htmx:pushedIntoHistory", this.syncFromHistoryEvent);
        document.body.removeEventListener("htmx:replacedInHistory", this.syncFromHistoryEvent);
        window.removeEventListener("popstate", this.syncFromLocation);
        document.body.removeEventListener("ticket-reply-sent", this.showReplyChat);
    }

    selectTicket(event: MouseEvent): void {
        if (!this.shouldHandleClick(event)) {
            return;
        }

        const target = event.currentTarget;
        if (target instanceof HTMLElement && target.dataset.ticketId) {
            this.element.dataset.currentTicketId = target.dataset.ticketId;
        }

        this.explicitPaneIntent = null;
        this.applyPane("detail");
    }

    showList(event?: Event): void {
        event?.preventDefault();
        this.rememberExplicitPaneIntent("list");
        this.applyPane("list", { pushHistory: true });
    }

    showDetail(event?: Event): void {
        event?.preventDefault();
        this.rememberExplicitPaneIntent("detail");
        this.applyPane("detail", { pushHistory: true });
    }

    showChat(event?: Event): void {
        event?.preventDefault();
        this.rememberExplicitPaneIntent("chat");
        this.applyPane("chat", { pushHistory: true });
    }

    showCurrentChat(event?: Event): void {
        event?.preventDefault();
        this.rememberExplicitPaneIntent("chat");
        this.applyPane("chat", { replaceHistory: true });
    }

    private syncFromLocation(event?: PopStateEvent): void {
        const statePane = this.mobilePaneFromState(event?.state);
        this.applyPane(statePane || this.paneFromUrl(window.location.href));
    }

    private syncFromHistoryEvent(event: Event): void {
        const path = (event as HtmxHistoryEvent).detail?.path || window.location.href;
        const pane = this.paneFromUrl(path);
        const explicitIntent = this.currentExplicitPaneIntent();

        if (explicitIntent && explicitIntent.pane !== pane && this.sameTicket(path, explicitIntent.ticketId)) {
            this.applyPane(explicitIntent.pane, { replaceHistory: true, replaceHistoryUrl: true });
            return;
        }

        if (!this.isCurrentUrl(path)) {
            return;
        }

        this.applyPane(pane, { replaceHistory: true });
    }

    private applyPane(
        pane: MobilePane,
        options: { pushHistory?: boolean; replaceHistory?: boolean; replaceHistoryUrl?: boolean } = {}
    ): void {
        this.element.dataset.mobilePane = pane;
        this.syncAria();

        if (options.pushHistory) {
            this.pushPaneHistory(pane);
        } else if (options.replaceHistory) {
            this.replacePaneHistory(pane, options.replaceHistoryUrl);
        }
    }

    private syncAria(): void {
        const mobile = this.mediaQuery?.matches ?? window.matchMedia(MOBILE_QUERY).matches;
        const pane = this.currentPane();

        this.setAriaHidden(this.listTargetOrNull(), mobile && pane !== "list");
        this.setAriaHidden(this.workspaceTargetOrNull(), mobile && pane === "list");
        this.setAriaHidden(this.detailTargetOrNull(), mobile && pane !== "detail");
        this.setAriaHidden(this.chatTargetOrNull(), mobile && pane !== "chat");
    }

    private pushPaneHistory(pane: MobilePane): void {
        const url = this.urlForPane(pane);
        window.history.pushState({ mobilePane: pane }, "", url);
    }

    private replacePaneHistory(pane: MobilePane, replaceUrl = false): void {
        window.history.replaceState(
            { ...(window.history.state || {}), mobilePane: pane },
            "",
            replaceUrl ? this.urlForPane(pane) : window.location.href
        );
    }

    private urlForPane(pane: MobilePane): string {
        const url = new URL(window.location.href);
        const ticketId = this.currentTicketId(url);

        if (pane === "list") {
            url.pathname = "/Tickets";
            url.searchParams.delete("ticketId");
            url.searchParams.delete("selectedTicketId");
        } else if (ticketId) {
            url.pathname = pane === "chat" ? "/ShowTicketTeamChat" : "/ShowTicket";
            url.searchParams.set("ticketId", ticketId);
            url.searchParams.delete("selectedTicketId");
        }

        return `${url.pathname}${url.search}${url.hash}`;
    }

    private isCurrentUrl(rawUrl: string): boolean {
        try {
            const url = new URL(rawUrl, window.location.origin);
            return `${url.pathname}${url.search}${url.hash}` === `${window.location.pathname}${window.location.search}${window.location.hash}`;
        } catch (_error) {
            return true;
        }
    }

    private sameTicket(rawUrl: string, ticketId: string): boolean {
        if (!ticketId) {
            return true;
        }

        try {
            const url = new URL(rawUrl, window.location.origin);
            return url.searchParams.get("ticketId") === ticketId;
        } catch (_error) {
            return true;
        }
    }

    private currentTicketId(url: URL): string {
        return this.element.dataset.currentTicketId || url.searchParams.get("ticketId") || "";
    }

    private paneFromUrl(rawUrl: string): MobilePane {
        let url: URL;

        try {
            url = new URL(rawUrl, window.location.origin);
        } catch (_error) {
            return this.initialPane();
        }

        if (url.pathname === "/Tickets") {
            return "list";
        }

        if (url.pathname === "/ShowTicketTeamChat") {
            return "chat";
        }

        return "detail";
    }

    private initialPane(): MobilePane {
        return this.normalizePane(this.initialPaneValue || this.element.dataset.mobilePane) || "list";
    }

    private currentPane(): MobilePane {
        return this.normalizePane(this.element.dataset.mobilePane) || this.initialPane();
    }

    private rememberExplicitPaneIntent(pane: MobilePane): void {
        const url = new URL(window.location.href);
        this.explicitPaneIntent = {
            pane,
            ticketId: this.currentTicketId(url),
            expiresAt: Date.now() + EXPLICIT_PANE_INTENT_TTL_MS,
        };
    }

    private currentExplicitPaneIntent(): ExplicitPaneIntent | null {
        if (!this.explicitPaneIntent || this.explicitPaneIntent.expiresAt < Date.now()) {
            this.explicitPaneIntent = null;
            return null;
        }

        return this.explicitPaneIntent;
    }

    private normalizePane(value: string | undefined): MobilePane | null {
        if (value === "list" || value === "detail" || value === "chat") {
            return value;
        }

        return null;
    }

    private mobilePaneFromState(state: unknown): MobilePane | null {
        if (typeof state !== "object" || state === null || !("mobilePane" in state)) {
            return null;
        }

        return this.normalizePane(String((state as { mobilePane?: unknown }).mobilePane || ""));
    }

    private setAriaHidden(element: HTMLElement | null, hidden: boolean): void {
        if (!element) {
            return;
        }

        element.setAttribute("aria-hidden", hidden ? "true" : "false");
    }

    private listTargetOrNull(): HTMLElement | null {
        return this.hasListTarget ? this.listTarget : null;
    }

    private workspaceTargetOrNull(): HTMLElement | null {
        return this.hasWorkspaceTarget ? this.workspaceTarget : null;
    }

    private detailTargetOrNull(): HTMLElement | null {
        return this.hasDetailTarget ? this.detailTarget : null;
    }

    private chatTargetOrNull(): HTMLElement | null {
        return this.hasChatTarget ? this.chatTarget : null;
    }

    private shouldHandleClick(event: MouseEvent): boolean {
        return (
            event.button === 0 &&
            !event.metaKey &&
            !event.ctrlKey &&
            !event.shiftKey &&
            !event.altKey &&
            !event.defaultPrevented
        );
    }
}
